import { FormEvent, useCallback, useEffect, useState } from 'react';
import { actorLabel, FieldError } from './DeliveryFeeForm';
import DisclaimerField from './DisclaimerField';
import InfoTip from './InfoTip';
import { COST_HINT } from './ProviderForm';
import {
  deliveryConfigApi, formIssues, issueFor, toValidationIssues, ValidationIssue, zonePriceField,
} from '../../services/deliveryConfigApi';
import {
  DEFAULT_ZOFEUR_EMIRATES, Emirate, EMIRATES, MAX_ZOFEUR_PRICE, MAX_ZOFEUR_THRESHOLD, ZOFEUR_CONFIG_MESSAGES,
  ZofeurConfig, ZofeurZone, ZONE_LABELS, zonePriceMessage, ZonePrices,
} from '../../types/delivery';
import {
  DEFAULT_DISCLAIMER_AR, DEFAULT_DISCLAIMER_EN, DISCLAIMER_MAX_LENGTH, INSURANCE_MESSAGES, MAX_INSURED_AMOUNT,
} from '../../utils/insuranceDisclaimer';

interface ZofeurConfigFormProps {
  token: string;
}

type LoadState =
  | { status: 'loading' }
  | { status: 'loaded'; config: ZofeurConfig }
  | { status: 'error'; message: string };

const hintStyle = { fontSize: '0.75rem', color: 'var(--admin-text-muted)', marginTop: '0.25rem' } as const;

/** "" is the unset state (a card saved before #1254) that the form refuses to submit. */
type VatChoice = '' | 'REGISTERED' | 'NOT_REGISTERED';

const TRN_RE = /^\d{15}$/;

function vatChoiceOf(config: ZofeurConfig): VatChoice {
  return config.vatRegistered === true ? 'REGISTERED' : config.vatRegistered === false ? 'NOT_REGISTERED' : '';
}

function asText(value: number | null | undefined): string {
  return value === null || value === undefined ? '' : String(value);
}

/** Spec 080 — the per-zone inputs, kept as typed text; a blank (absent) zone pays the default. */
type ZonePriceText = Partial<Record<ZofeurZone, string>>;

function zonePriceText(prices: ZonePrices | undefined): ZonePriceText {
  const text: ZonePriceText = {};
  for (const [zone, price] of Object.entries(prices ?? {}) as [ZofeurZone, number | undefined][]) {
    if (typeof price === 'number') text[zone] = String(price);
  }
  return text;
}

/** Spec 080 — the rows under the checkboxes: each ticked emirate, with Al Ain right under Abu Dhabi. */
function zoneRowsFor(emirates: Emirate[]): ZofeurZone[] {
  return EMIRATES.map((e) => e.code)
    .filter((code) => emirates.includes(code))
    .flatMap((code): ZofeurZone[] => (code === 'ABU_DHABI' ? ['ABU_DHABI', 'AL_AIN'] : [code]));
}

/** A whole number of dirhams from the text, or null when it is blank or not one. */
function wholeDirhams(text: string | undefined): number | null {
  const trimmed = (text ?? '').trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  return Number.isInteger(n) && n >= 1 && n <= MAX_ZOFEUR_PRICE ? n : null;
}

/** The checkbox state for a loaded config: the saved emirates, or Flow A's default before the first save. */
function initialEmirates(config: ZofeurConfig): Emirate[] {
  return config.emirates && config.emirates.length > 0 ? config.emirates : [...DEFAULT_ZOFEUR_EMIRATES];
}

/**
 * Spec 072 v2 Flow A (AC-1, AC-2) — the Zofeur card on Finance → Delivery & Recovery.
 *
 * Mirrors `DeliveryFeeForm`, with three settings: the price for pickup + drop-off together (VAT-inclusive,
 * whole dirhams so it splits evenly into the order's two trips, D21), the service price above which
 * Zofeur is free (blank = never free), and the emirates it is offered in. Like the delivery fee these
 * are snapshotted onto a booking, so a change reaches new bookings only.
 *
 * It loads its own config: the card fails on its own, and the Recovery fee and the provider registry
 * above and below it stay usable when the Zofeur endpoint does not answer.
 *
 * #1254 — Zofeur is set up here and nowhere else: the card also holds what the Zofeur row in the
 * provider registry used to (VAT status, cost per trip, the insurance cover and the customer's
 * disclaimer, with the provider form's rules and live preview). Orders keeps a hidden provider row in
 * step with the card, and a Zofeur leg is assigned that row; a leg already assigned keeps its snapshot.
 *
 * Spec 080 (#1314) — "Price per emirate": under the checkboxes, one optional price per ticked emirate,
 * plus Al Ain under Abu Dhabi. Blank pays the default price (Al Ain: Abu Dhabi's, then the default),
 * which the placeholders spell out. Unticking an emirate drops its price (and Al Ain's with Abu Dhabi).
 */
export default function ZofeurConfigForm({ token }: ZofeurConfigFormProps) {
  const [load, setLoad] = useState<LoadState>({ status: 'loading' });
  const [price, setPrice] = useState('');
  const [threshold, setThreshold] = useState('');
  const [emirates, setEmirates] = useState<Emirate[]>([...DEFAULT_ZOFEUR_EMIRATES]);
  const [zonePrices, setZonePrices] = useState<ZonePriceText>({});
  const [vatChoice, setVatChoice] = useState<VatChoice>('');
  const [trn, setTrn] = useState('');
  const [costPerTrip, setCostPerTrip] = useState('');
  const [insuredAmount, setInsuredAmount] = useState('');
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [disclaimerEn, setDisclaimerEn] = useState('');
  const [disclaimerAr, setDisclaimerAr] = useState('');
  const [saving, setSaving] = useState(false);
  const [issues, setIssues] = useState<ValidationIssue[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const apply = useCallback((config: ZofeurConfig) => {
    setLoad({ status: 'loaded', config });
    setPrice(asText(config.price));
    setThreshold(asText(config.threshold));
    setEmirates(initialEmirates(config));
    setZonePrices(zonePriceText(config.zonePrices));
    setVatChoice(vatChoiceOf(config));
    setTrn(config.trn ?? '');
    setCostPerTrip(asText(config.costPerTrip));
    setInsuredAmount(asText(config.insuredAmount));
    setShowDisclaimer(config.showInsuranceDisclaimer ?? false);
    setDisclaimerEn(config.disclaimerEn ?? '');
    setDisclaimerAr(config.disclaimerAr ?? '');
  }, []);

  const fetchConfig = useCallback(async () => {
    setLoad({ status: 'loading' });
    try {
      apply(await deliveryConfigApi.getZofeurConfig(token));
    } catch (err) {
      setLoad({ status: 'error', message: err instanceof Error ? err.message : 'Failed to load the Zofeur settings' });
    }
  }, [token, apply]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const toggleEmirate = (code: Emirate, on: boolean) => {
    // Kept in the EMIRATES order, so the request reads the same whichever box was ticked first.
    setEmirates((current) => EMIRATES.map((e) => e.code).filter((c) => (c === code ? on : current.includes(c))));
    // Spec 080 (AC-3) — an unticked emirate loses its price; Al Ain goes with Abu Dhabi.
    if (!on) {
      setZonePrices((current) => {
        const next = { ...current };
        delete next[code];
        if (code === 'ABU_DHABI') delete next.AL_AIN;
        return next;
      });
    }
  };

  const setZonePrice = (zone: ZofeurZone, value: string) => {
    setZonePrices((current) => ({ ...current, [zone]: value }));
  };

  // Requirements §7: the defaults arrive the first time the toggle goes on with nothing written yet.
  const toggleDisclaimer = (on: boolean) => {
    setShowDisclaimer(on);
    if (on && !disclaimerEn.trim() && !disclaimerAr.trim()) {
      setDisclaimerEn(DEFAULT_DISCLAIMER_EN);
      setDisclaimerAr(DEFAULT_DISCLAIMER_AR);
    }
  };

  const parsedInsured = insuredAmount.trim() === '' ? null : Number(insuredAmount);
  const previewAmount = parsedInsured !== null && Number.isFinite(parsedInsured) ? parsedInsured : null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaved(false);
    setError(null);

    // Design §3 — the same rules the server answers 422 for, checked first so nothing is sent.
    const local: ValidationIssue[] = [];
    const parsedPrice = price.trim() === '' ? NaN : Number(price.trim());
    if (!Number.isInteger(parsedPrice) || parsedPrice < 1 || parsedPrice > MAX_ZOFEUR_PRICE) {
      local.push({ field: 'price', message: ZOFEUR_CONFIG_MESSAGES.price });
    }
    let parsedThreshold: number | null = null;
    if (threshold.trim() !== '') {
      parsedThreshold = Number(threshold.trim());
      if (!Number.isFinite(parsedThreshold) || parsedThreshold < 0 || parsedThreshold > MAX_ZOFEUR_THRESHOLD) {
        local.push({ field: 'threshold', message: ZOFEUR_CONFIG_MESSAGES.threshold });
      }
    }
    if (emirates.length === 0) local.push({ field: 'emirates', message: ZOFEUR_CONFIG_MESSAGES.emirates });

    // Spec 080 (DD11) — each zone's own price, optional; the server's words, under its row.
    const sentZonePrices: ZonePrices = {};
    for (const zone of zoneRowsFor(emirates)) {
      const text = (zonePrices[zone] ?? '').trim();
      if (text === '') continue;
      const value = wholeDirhams(text);
      if (value === null) local.push({ field: zonePriceField(zone), message: zonePriceMessage(zone) });
      else sentZonePrices[zone] = value;
    }

    // #1254 — the provider form's rules, in the server's words.
    const cleanedTrn = trn.replace(/\s/g, '');
    if (vatChoice === '') local.push({ field: 'trn', message: ZOFEUR_CONFIG_MESSAGES.vat });
    else if (vatChoice === 'REGISTERED' && !TRN_RE.test(cleanedTrn)) {
      local.push({ field: 'trn', message: ZOFEUR_CONFIG_MESSAGES.trnFormat });
    }
    let cost: number | null = null;
    if (costPerTrip.trim() !== '') {
      cost = Number(costPerTrip.trim());
      if (!Number.isInteger(cost) || cost < 0) local.push({ field: 'costPerTrip', message: ZOFEUR_CONFIG_MESSAGES.cost });
    }
    let amount: number | null = null;
    if (insuredAmount.trim() !== '') {
      amount = Number(insuredAmount.trim());
      if (!Number.isInteger(amount) || amount < 0 || amount > MAX_INSURED_AMOUNT) {
        local.push({ field: 'insuredAmount', message: INSURANCE_MESSAGES.amountRange });
      }
    }
    if (disclaimerEn.trim().length > DISCLAIMER_MAX_LENGTH || disclaimerAr.trim().length > DISCLAIMER_MAX_LENGTH) {
      local.push({ field: 'disclaimer', message: INSURANCE_MESSAGES.textLength });
    } else if (showDisclaimer && (!disclaimerEn.trim() || !disclaimerAr.trim())) {
      local.push({ field: 'disclaimer', message: INSURANCE_MESSAGES.textsRequired });
    }
    if (showDisclaimer && !local.some((i) => i.field === 'insuredAmount') && !(amount !== null && amount > 0)) {
      local.push({ field: 'insuredAmount', message: INSURANCE_MESSAGES.amountRequired });
    }
    if (local.length > 0) { setIssues(local); return; }

    setIssues([]);
    setSaving(true);
    try {
      const updated = await deliveryConfigApi.updateZofeurConfig(
        {
          price: parsedPrice,
          threshold: parsedThreshold,
          emirates,
          trn: vatChoice === 'REGISTERED' ? cleanedTrn : null,
          vatRegistered: vatChoice === 'REGISTERED',
          costPerTrip: cost,
          insuredAmount: amount,
          showInsuranceDisclaimer: showDisclaimer,
          disclaimerEn: disclaimerEn.trim(),
          disclaimerAr: disclaimerAr.trim(),
          zonePrices: sentZonePrices,
        },
        token,
      );
      apply(updated);
      setSaved(true);
    } catch (err) {
      const parsed = toValidationIssues(err);
      if (parsed.length > 0) setIssues(parsed);
      else setError(err instanceof Error ? err.message : 'Failed to save the Zofeur settings');
    } finally {
      setSaving(false);
    }
  };

  if (load.status === 'loading') {
    return (
      <div className="admin-card" style={{ marginTop: '1rem' }} data-testid="zofeur-config">
        <h3 className="admin-card-title">Zofeur driver pickup &amp; drop-off</h3>
        <div className="admin-loading" data-testid="zofeur-loading"><div className="admin-loading-spinner"></div></div>
      </div>
    );
  }

  if (load.status === 'error') {
    return (
      <div className="admin-card" style={{ marginTop: '1rem' }} data-testid="zofeur-config">
        <h3 className="admin-card-title">Zofeur driver pickup &amp; drop-off</h3>
        <div className="admin-info-banner danger" role="alert" data-testid="zofeur-load-error">{load.message}</div>
        <button type="button" className="admin-btn admin-btn-sm admin-btn-outline" onClick={fetchConfig}>Try again</button>
      </div>
    );
  }

  const { config } = load;
  const actor = actorLabel(config);
  const formLevel = formIssues(issues);
  const emiratesError = issueFor(issues, 'emirates');
  // Spec 080 — what a blank zone pays, as the placeholders say: the default as typed (else as saved).
  const defaultPrice = wholeDirhams(price) ?? config.price;
  const abuDhabiPrice = wholeDirhams(zonePrices.ABU_DHABI) ?? defaultPrice;
  const zonePlaceholder = (zone: ZofeurZone) => {
    if (zone === 'AL_AIN') return abuDhabiPrice === null ? 'Same as Abu Dhabi' : `Same as Abu Dhabi (AED ${abuDhabiPrice})`;
    return defaultPrice === null ? 'Default' : `Default (AED ${defaultPrice})`;
  };
  const zoneRows = zoneRowsFor(emirates);

  return (
    <div className="admin-card" style={{ marginTop: '1rem' }} data-testid="zofeur-config">
      <div className="admin-card-header">
        <h3 className="admin-card-title">Zofeur driver pickup &amp; drop-off</h3>
        {config.updatedAt && (
          <span style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)' }} data-testid="zofeur-last-changed">
            Last changed {new Date(config.updatedAt).toLocaleString()}
            {actor ? ` by ${actor}` : ''}
          </span>
        )}
      </div>

      <div className="admin-info-banner info" role="note">
        A Zofeur driver drives the customer&rsquo;s car to the garage and back. Changes apply to{' '}
        <strong>new bookings only</strong>.
        {config.price === null && ' Customers are not offered Zofeur until a price is saved.'}
        {config.price !== null && (config.vatRegistered === null || config.vatRegistered === undefined)
          && ' Customers are not offered Zofeur until the card is saved with Zofeur\u2019s VAT status.'}
      </div>

      {error && <div className="admin-info-banner danger" role="alert" data-testid="zofeur-error">{error}</div>}
      {formLevel.length > 0 && (
        <div className="admin-info-banner danger" role="alert" data-testid="zofeur-form-error">{formLevel.join(' ')}</div>
      )}
      {saved && <div className="admin-info-banner success" role="status" data-testid="zofeur-saved">Saved. New bookings from now on use these settings.</div>}

      {/* noValidate: the three messages below are the server's own words (design §3); the browser's
          min/max bubbles would show different ones first. */}
      <form onSubmit={submit} aria-label="Zofeur configuration" noValidate>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div className="admin-field" style={{ margin: 0 }}>
            <label className="admin-field-label" htmlFor="zofeur-price">Price for pickup + drop-off (AED, incl. VAT)</label>
            <input
              id="zofeur-price"
              data-testid="zofeur-price"
              type="number"
              step="1"
              min="1"
              max={MAX_ZOFEUR_PRICE}
              inputMode="numeric"
              className="admin-search-input"
              style={{ width: '14rem' }}
              placeholder="e.g. 150"
              value={price}
              disabled={saving}
              aria-invalid={!!issueFor(issues, 'price')}
              aria-describedby={issueFor(issues, 'price') ? 'zofeur-price-hint zofeur-price-error' : 'zofeur-price-hint'}
              onChange={(e) => setPrice(e.target.value)}
            />
            <div id="zofeur-price-hint" style={hintStyle}>Whole dirhams; both trips together</div>
            <FieldError id="zofeur-price-error" message={issueFor(issues, 'price')} />
          </div>

          <div className="admin-field" style={{ margin: 0 }}>
            <label className="admin-field-label" htmlFor="zofeur-threshold">Free when the service price is above (AED, optional)</label>
            <input
              id="zofeur-threshold"
              data-testid="zofeur-threshold"
              type="number"
              step="0.01"
              min="0"
              max={MAX_ZOFEUR_THRESHOLD}
              inputMode="decimal"
              className="admin-search-input"
              style={{ width: '14rem' }}
              placeholder="Blank = never free"
              value={threshold}
              disabled={saving}
              aria-invalid={!!issueFor(issues, 'threshold')}
              aria-describedby={issueFor(issues, 'threshold') ? 'zofeur-threshold-hint zofeur-threshold-error' : 'zofeur-threshold-hint'}
              onChange={(e) => setThreshold(e.target.value)}
            />
            <div id="zofeur-threshold-hint" style={hintStyle}>Leave blank and Zofeur is always charged</div>
            <FieldError id="zofeur-threshold-error" message={issueFor(issues, 'threshold')} />
          </div>
        </div>

        <fieldset
          aria-invalid={!!emiratesError}
          aria-describedby={emiratesError ? 'zofeur-emirates-error' : undefined}
          style={{ border: 'none', padding: 0, margin: '1rem 0' }}
        >
          <legend className="admin-field-label">Emirates offered</legend>
          <div data-testid="zofeur-emirates" style={{ display: 'flex', gap: '0.5rem 1rem', flexWrap: 'wrap' }}>
            {EMIRATES.map(({ code, label }) => (
              <label key={code} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', minHeight: '1.75rem' }}>
                <input
                  type="checkbox"
                  id={`zofeur-emirate-${code}`}
                  data-testid={`zofeur-emirate-${code}`}
                  checked={emirates.includes(code)}
                  disabled={saving}
                  onChange={(e) => toggleEmirate(code, e.target.checked)}
                />
                {label}
              </label>
            ))}
          </div>
          <div style={hintStyle}>Checked at the pickup address: a customer outside these emirates can&rsquo;t book Zofeur.</div>
          <FieldError id="zofeur-emirates-error" message={emiratesError} />
        </fieldset>

        {/* Spec 080 (#1314) — an optional price per ticked emirate, Al Ain under Abu Dhabi. */}
        {zoneRows.length > 0 && (
          <section data-testid="zofeur-zone-prices" aria-labelledby="zofeur-zone-prices-title" style={{ margin: '0 0 1rem' }}>
            <h4 id="zofeur-zone-prices-title" style={{ margin: '0 0 0.25rem', fontSize: '0.875rem' }}>Price per emirate</h4>
            <div style={{ ...hintStyle, marginTop: 0, marginBottom: '0.5rem' }}>
              Optional, AED incl. VAT, whole dirhams, both trips together. Blank uses the default price; the
              customer pays the price of their pickup address&rsquo;s emirate.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {zoneRows.map((zone) => {
                const zoneError = issueFor(issues, zonePriceField(zone));
                return (
                  <div
                    key={zone}
                    data-testid={`zofeur-zone-row-${zone}`}
                    style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', flexWrap: 'wrap' }}
                  >
                    <label
                      htmlFor={`zofeur-zone-price-${zone}`}
                      style={{ width: '9rem', paddingTop: '0.4rem', paddingLeft: zone === 'AL_AIN' ? '1rem' : 0, fontSize: '0.875rem' }}
                    >
                      {ZONE_LABELS[zone]}
                      {zone === 'AL_AIN' && <span style={{ ...hintStyle, display: 'block', marginTop: 0 }}>inside Abu Dhabi</span>}
                    </label>
                    <div>
                      <input
                        id={`zofeur-zone-price-${zone}`}
                        data-testid={`zofeur-zone-price-${zone}`}
                        aria-label={`${ZONE_LABELS[zone]} price (AED)`}
                        type="number"
                        step="1"
                        min="1"
                        max={MAX_ZOFEUR_PRICE}
                        inputMode="numeric"
                        className="admin-search-input"
                        style={{ width: '16rem' }}
                        placeholder={zonePlaceholder(zone)}
                        value={zonePrices[zone] ?? ''}
                        disabled={saving}
                        aria-invalid={!!zoneError}
                        aria-describedby={zoneError ? `zofeur-zone-price-${zone}-error` : undefined}
                        onChange={(e) => setZonePrice(zone, e.target.value)}
                      />
                      <FieldError id={`zofeur-zone-price-${zone}-error`} message={zoneError} />
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* #1254 — what the Zofeur row in the provider registry used to hold. */}
        <section
          data-testid="zofeur-provider"
          aria-labelledby="zofeur-provider-title"
          style={{ borderTop: '1px solid var(--admin-border)', paddingTop: '1rem', marginBottom: '1rem' }}
        >
          <h4 id="zofeur-provider-title" style={{ margin: '0 0 0.25rem', fontSize: '0.875rem' }}>Zofeur as a provider</h4>
          <div style={{ ...hintStyle, marginTop: 0, marginBottom: '0.75rem' }}>
            Assign driver uses these on every Zofeur leg; a leg already assigned keeps what it was assigned with.
            Zofeur trips are paid under Settlements &rarr; Providers.
          </div>

          <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div className="admin-field" style={{ margin: 0 }}>
              <span className="admin-field-label" id="zofeur-vat-label">VAT status</span>
              <div role="radiogroup" aria-labelledby="zofeur-vat-label" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
                  <input type="radio" name="zofeur-vat" data-testid="zofeur-vat-registered" checked={vatChoice === 'REGISTERED'}
                    disabled={saving} onChange={() => setVatChoice('REGISTERED')} />
                  VAT registered — TRN
                </label>
                <input
                  id="zofeur-trn"
                  data-testid="zofeur-trn"
                  className="admin-search-input"
                  style={{ width: '14rem' }}
                  aria-label="TRN"
                  placeholder="15 digits"
                  inputMode="numeric"
                  value={trn}
                  disabled={saving || vatChoice !== 'REGISTERED'}
                  aria-invalid={!!issueFor(issues, 'trn')}
                  aria-describedby={issueFor(issues, 'trn') ? 'zofeur-trn-error' : undefined}
                  onChange={(e) => setTrn(e.target.value)}
                />
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
                  <input type="radio" name="zofeur-vat" data-testid="zofeur-vat-not-registered" checked={vatChoice === 'NOT_REGISTERED'}
                    disabled={saving} onChange={() => setVatChoice('NOT_REGISTERED')} />
                  Not VAT registered
                </label>
              </div>
              <FieldError id="zofeur-trn-error" message={issueFor(issues, 'trn')} />
            </div>

            <div className="admin-field" style={{ margin: 0 }}>
              <label className="admin-field-label" htmlFor="zofeur-cost">
                Cost per trip, ex. VAT (AED, optional)
                <InfoTip glossaryKey="providerCostPerTrip" />
              </label>
              <input
                id="zofeur-cost"
                data-testid="zofeur-cost"
                type="number"
                step="1"
                min="0"
                inputMode="numeric"
                className="admin-search-input"
                style={{ width: '14rem' }}
                placeholder="Blank = agreed at payment"
                value={costPerTrip}
                disabled={saving}
                aria-invalid={!!issueFor(issues, 'costPerTrip')}
                aria-describedby={issueFor(issues, 'costPerTrip') ? 'zofeur-cost-hint zofeur-cost-error' : 'zofeur-cost-hint'}
                onChange={(e) => setCostPerTrip(e.target.value)}
              />
              <div id="zofeur-cost-hint" style={{ ...hintStyle, maxWidth: '18rem' }}>
                Whole dirhams. {COST_HINT} Garagat&rsquo;s cost, never shown to customers.
              </div>
              <FieldError id="zofeur-cost-error" message={issueFor(issues, 'costPerTrip')} />
            </div>
          </div>

          <h4 style={{ margin: '1rem 0 0.5rem', fontSize: '0.875rem' }}>Insurance</h4>
          <div className="admin-field" style={{ margin: 0 }}>
            <label className="admin-field-label" htmlFor="zofeur-insured-amount">Insured amount, AED (optional)</label>
            <input
              id="zofeur-insured-amount"
              data-testid="zofeur-insured-amount"
              type="number"
              step="1"
              min="0"
              max={MAX_INSURED_AMOUNT}
              inputMode="numeric"
              className="admin-search-input"
              style={{ width: '14rem' }}
              placeholder="e.g. 350000"
              value={insuredAmount}
              disabled={saving}
              aria-invalid={!!issueFor(issues, 'insuredAmount')}
              aria-describedby={issueFor(issues, 'insuredAmount') ? 'zofeur-insured-amount-error' : undefined}
              onChange={(e) => setInsuredAmount(e.target.value)}
            />
            <FieldError id="zofeur-insured-amount-error" message={issueFor(issues, 'insuredAmount')} />
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', margin: '0.75rem 0 0.25rem' }}>
            <input
              type="checkbox"
              data-testid="zofeur-show-disclaimer"
              checked={showDisclaimer}
              disabled={saving}
              onChange={(e) => toggleDisclaimer(e.target.checked)}
            />
            Show insurance disclaimer to customer
          </label>
          <div style={{ ...hintStyle, marginBottom: '0.75rem' }}>
            Shown in the app under the driver&rsquo;s details on Zofeur trips, once the driver confirms; the
            driver&rsquo;s phone is then required at Assign driver. <code>{'{amount}'}</code> is filled in from the insured amount.
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.75rem' }}>
            <DisclaimerField
              idPrefix="zofeur"
              lang="en"
              label="Disclaimer (English)"
              value={disclaimerEn}
              amount={previewAmount}
              disabled={saving}
              invalid={!!issueFor(issues, 'disclaimer')}
              onChange={setDisclaimerEn}
            />
            <DisclaimerField
              idPrefix="zofeur"
              lang="ar"
              label="Disclaimer (Arabic)"
              value={disclaimerAr}
              amount={previewAmount}
              disabled={saving}
              invalid={!!issueFor(issues, 'disclaimer')}
              onChange={setDisclaimerAr}
            />
          </div>
          <FieldError id="zofeur-disclaimer-error" message={issueFor(issues, 'disclaimer')} />
        </section>

        <button type="submit" className="admin-btn admin-btn-sm admin-btn-primary" disabled={saving}>
          {saving ? 'Saving…' : 'Save Zofeur settings'}
        </button>
      </form>
    </div>
  );
}
